/**
 * Settings — Reference lists (list)
 *
 * One editor over every managed reference list in core.reference (slogans,
 * delivery time, offer validity, packaging translations, …), chosen via the
 * module→category picker in the header. Row-click navigates to the shared editor;
 * count / search / "+" live in the header record-nav.
 */

import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useLocale, useTranslations } from 'gt-react'
import { useEffect, useMemo, useRef } from 'react'
import { EntityList, type EntityListColumn } from '@/components/crm/entity-list'
import { positionJumpTarget } from '@/components/data-table/list-state'
import { useServerList } from '@/components/data-table/use-server-list'
import { useLastOpened } from '@/components/layout/record-nav-context'
import { useEntityListNav } from '@/components/layout/use-entity-nav'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { lookupLabel } from '@/lib/crm/lookup-label'
import { orpc } from '@/lib/orpcClient'
import { listFromSearch } from './-reference-fields'

export const Route = createFileRoute('/settings/reference/')({
  component: ReferenceListPage,
  validateSearch: (search: Record<string, unknown>): { list?: string } => ({
    list: typeof search.list === 'string' ? search.list : undefined,
  }),
})

interface ReferenceRow {
  id: string
  value: Record<string, string>
  sortOrder: number
  active: boolean
}

function ReferenceListPage() {
  const t = useTranslations()
  const locale = useLocale()
  const navigate = useNavigate()
  const { lastId, lastPosition } = useLastOpened('reference')
  // De gekozen lijst staat in de URL, zie `listFromSearch`.
  const selected = listFromSearch(Route.useSearch())
  const setSelected = (next: string) =>
    void navigate({ to: '/settings/reference', search: { list: next } })
  const [module, category] = selected.split('/')

  // Zoeken, sorteren en pagineren gebeuren in de database; deze pagina rendert
  // wat er terugkomt. Zie components/data-table/use-server-list.
  const list = useServerList({
    entityKey: 'reference',
    initialSorting: [{ id: 'sortOrder', desc: false }],
  })

  const catsQ = useQuery(orpc.reference.categories.queryOptions())
  const cats = (catsQ.data ?? []) as Array<{
    module: string
    category: string
    labelKey: string | null
  }>
  const listQ = useQuery(
    orpc.reference.list.queryOptions({
      input: {
        ...list.input,
        module: module ?? '',
        category: category ?? '',
        includeInactive: true,
      },
    }),
  )
  const rows = (listQ.data?.rows ?? []) as ReferenceRow[]
  const total = listQ.data?.total
  useEffect(() => {
    list.setTotal(total)
  }, [total, list.setTotal])

  // Switching to a different reference list is a new found set — start back on
  // its first page, exactly like a new search or sort already does inside
  // useServerList. Without this, a page index left over from a bigger list can
  // point past the end of a smaller one and render empty.
  useEffect(() => {
    list.goToPosition(1)
  }, [selected, list.goToPosition])

  // Terug van een record: spring naar de pagina waar het staat, tenzij het daar
  // toevallig al staat. Precies één keer per aankomst — de ref voorkomt dat een
  // latere eigen paginering, zoekactie of sortering (waarbij het record terecht
  // niet meer in `rows` zit) alsnog terugspringt.
  const jumpedToPositionRef = useRef<string | null>(null)
  useEffect(() => {
    if (lastId == null || lastPosition == null || jumpedToPositionRef.current === lastId) return
    jumpedToPositionRef.current = lastId
    const target = positionJumpTarget(
      lastPosition,
      rows.some((r) => r.id === lastId),
    )
    if (target != null) list.goToPosition(target)
  }, [lastId, lastPosition, rows, list.goToPosition])

  useEntityListNav({
    entityKey: 'reference',
    total: total ?? 0,
    toNew: () =>
      navigate({
        to: '/settings/reference/$module/$category/new',
        params: { module: module ?? '', category: category ?? '' },
      }),
    search: list.search,
    onSearch: list.setSearch,
    sort: list.sort,
  })

  const columns = useMemo<EntityListColumn<ReferenceRow>[]>(
    () => [
      {
        key: 'sortOrder',
        header: t('crm.settings.lookups.sortOrder'),
        render: (r) => r.sortOrder,
        sortAccessor: (r) => r.sortOrder,
      },
      {
        key: 'id',
        header: t('crm.settings.lookups.key'),
        render: (r) => <code className="text-xs text-muted-foreground">{r.id}</code>,
        sortAccessor: (r) => r.id,
      },
      {
        key: 'label',
        header: t('crm.settings.lookups.name'),
        render: (r) => <span className="font-medium">{lookupLabel(r, locale)}</span>,
        sortAccessor: (r) => lookupLabel(r, locale),
      },
      {
        key: 'active',
        header: t('crm.settings.lookups.active'),
        render: (r) => (
          <Badge variant="outline">
            {r.active ? t('crm.settings.lookups.active') : t('common.inactive')}
          </Badge>
        ),
        sortAccessor: (r) => (r.active ? 1 : 0),
      },
    ],
    [t, locale],
  )

  const catLabel = (c: { module: string; category: string; labelKey: string | null }) =>
    c.labelKey ? t(c.labelKey) : `${c.module} · ${c.category}`

  return (
    <EntityList<ReferenceRow>
      title={t('reference.title')}
      rows={rows}
      columns={columns}
      isLoading={listQ.isLoading}
      error={listQ.error}
      highlightId={lastId}
      server={list.server}
      actions={
        <Select value={selected} onValueChange={setSelected}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder={t('reference.chooseList')} />
          </SelectTrigger>
          <SelectContent>
            {[...cats]
              .sort((a, b) => catLabel(a).localeCompare(catLabel(b), locale))
              .map((c) => (
                <SelectItem key={`${c.module}/${c.category}`} value={`${c.module}/${c.category}`}>
                  {catLabel(c)}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      }
      onRowClick={(row) =>
        navigate({
          to: '/settings/reference/$module/$category/$id',
          params: { module: module ?? '', category: category ?? '', id: row.id },
        })
      }
    />
  )
}
